import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Loader2, AlertCircle, Mic, Video, Camera, Play, X, Backpack, Plus, Users, Tent, Trophy, Medal } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import HubLayout from './common/HubLayout';
import WhoseStorySelector from './stories/WhoseStorySelector';
import StoryTypeGrid from './stories/StoryTypeGrid';
import ContextBanner from './stories/ContextBanner';
import MediaCapture from './MediaCapture';
import { axiosInstance } from '../services/api';
import { createWithMedia } from '../services/mediaService';
import {
  schoolYearOf, gradeForYear, gradeLabel, schoolYearRange,
  isSchoolAge, kStartYearFromGrade, defaultKStartYear,
} from '../utils/schoolYears';
import ChangeGradeModal from './schoolyears/ChangeGradeModal';

// ── Accent colour palettes ───────────────────────────────────────────────
const ACCENT = {
  olive: {
    color: 'text-primary-green',
    border: 'border-primary-green',
    bg: 'bg-green-50',
    bgSolid: 'bg-primary-green',
    ring: 'ring-primary-green',
    check: 'bg-primary-green border-primary-green',
    shadow: 'shadow-green-100',
  },
  orange: {
    color: 'text-highlight-orange',
    border: 'border-highlight-orange',
    bg: 'bg-orange-50',
    bgSolid: 'bg-highlight-orange',
    ring: 'ring-highlight-orange',
    check: 'bg-highlight-orange border-highlight-orange',
    shadow: 'shadow-orange-100',
  },
  blue: {
    color: 'text-blue-700',
    border: 'border-blue-600',
    bg: 'bg-blue-50',
    bgSolid: 'bg-blue-600',
    ring: 'ring-blue-600',
    check: 'bg-blue-600 border-blue-600',
    shadow: 'shadow-blue-100',
  },
  teal: {
    color: 'text-teal-700',
    border: 'border-teal-600',
    bg: 'bg-teal-50',
    bgSolid: 'bg-teal-600',
    ring: 'ring-teal-600',
    check: 'bg-teal-600 border-teal-600',
    shadow: 'shadow-teal-100',
  },
};

function getActiveAccent(selectedChildIds, children) {
  if (selectedChildIds.length === 0) return ACCENT.orange; // default placeholder
  if (selectedChildIds.length > 1) return ACCENT.teal;
  const child = children.find(c => c.id === selectedChildIds[0]);
  return ACCENT[child?.accentColor ?? 'orange'];
}

function deriveAccent(tier, selectedChildIds, children) {
  if (tier !== 'child') return ACCENT.olive;
  return getActiveAccent(selectedChildIds, children);
}

// ── Story template ID (same as existing) ─────────────────────────────────
const STORY_TEMPLATE_ID = 12;

// ── Dynamic text helpers ─────────────────────────────────────────────────
function getBannerText(tier, selectedChildIds, children) {
  if (tier === 'mine') {
    return 'This story will be saved into your personal life story and shared with your family.';
  }
  if (selectedChildIds.length === 0) {
    return 'Select one or more children above.';
  }
  if (selectedChildIds.length === 1) {
    const child = children.find(c => c.id === selectedChildIds[0]);
    const name = child?.first_name || 'your child';
    return `You are writing this as ${name}'s guardian. It will appear in ${name}'s life story chapters.`;
  }
  const names = selectedChildIds
    .map(id => children.find(c => c.id === id)?.first_name || 'child')
    .join(' and ');
  return `This story will appear in both ${names}'s life story chapters \u2014 saved once, linked to both.`;
}

function getTitlePlaceholder(tier, selectedChildIds, children) {
  if (tier === 'mine') return 'e.g. The summer we built the back porch';
  if (selectedChildIds.length === 1) {
    const child = children.find(c => c.id === selectedChildIds[0]);
    const name = child?.first_name || 'your child';
    return `e.g. ${name}'s first day of school`;
  }
  return 'e.g. Our road trip to Yellowstone';
}

function getBodyLabel(tier, selectedChildIds, children) {
  if (tier === 'mine') return 'Tell the story';
  if (selectedChildIds.length === 1) {
    const child = children.find(c => c.id === selectedChildIds[0]);
    const name = child?.first_name || 'your child';
    return `Write ${name}'s story`;
  }
  return 'Write the story';
}

function getBodyPlaceholder(tier) {
  if (tier === 'mine') {
    return 'What happened? How did it feel? What do you want your family to remember about this moment?';
  }
  return 'What did they do? How did they react? What made this moment worth keeping forever?';
}

function getSectionSubtitle(tier, selectedChildIds, children) {
  if (tier === 'mine') return "Tell it in your own words \u2014 don't worry about perfect spelling or grammar";
  if (selectedChildIds.length === 1) {
    const child = children.find(c => c.id === selectedChildIds[0]);
    const name = child?.first_name || 'your child';
    return `Describe what ${name} did or experienced`;
  }
  return "Describe what happened \u2014 this will appear in each child's story";
}

function getSaveLabel(tier, selectedChildIds, children) {
  if (tier === 'mine') return 'Save My Story';
  if (selectedChildIds.length === 0) return 'Select a Child First';
  if (selectedChildIds.length === 1) {
    const child = children.find(c => c.id === selectedChildIds[0]);
    const name = child?.first_name || "Child's";
    return `Save ${name}'s Story`;
  }
  return 'Save to Both Stories';
}

const RequiredMark = () => (
  <span className="text-red-600 ml-1" aria-hidden="true" title="Required">*</span>
);

// ── Component ────────────────────────────────────────────────────────────
const TellAStory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ── Form state ───────────────────────────────────────────────────────
  const [tier, setTier] = useState('mine');
  const [children, setChildren] = useState([]);
  const [selectedChildIds, setSelectedChildIds] = useState([]);
  const [storyType, setStoryType] = useState('');
  const [title, setTitle] = useState('');
  const [when, setWhen] = useState('');
  const [where, setWhere] = useState('');
  const [body, setBody] = useState('');
  const [localMediaFiles, setLocalMediaFiles] = useState([]);

  // ── School year state ────────────────────────────────────────────────
  const [schoolYearOverride, setSchoolYearOverride] = useState(null);
  const [isGradePickerOpen, setIsGradePickerOpen] = useState(false);
  const [selectedActivityId, setSelectedActivityId] = useState(null);
  const [newActivityOpen, setNewActivityOpen] = useState(false);
  const [newActivity, setNewActivity] = useState({ category: '', name: '', teamName: '' });
  const [childActivities, setChildActivities] = useState([]);

  // ── UI state ─────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loadingChildren, setLoadingChildren] = useState(false);
  const [isMediaCaptureOpen, setIsMediaCaptureOpen] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);

  // ── Refs for scroll-to-error ─────────────────────────────────────────
  const sectionRefs = {
    subject: useRef(null),
    storyType: useRef(null),
    title: useRef(null),
    when: useRef(null),
    where: useRef(null),
    body: useRef(null),
  };

  // ── Accent colour derivation ─────────────────────────────────────────
  const accentClasses = deriveAccent(tier, selectedChildIds, children);

  // ── Pre-fill from URL params ─────────────────────────────────────────
  useEffect(() => {
    const paramStoryType = searchParams.get('storyType');
    const paramTitle = searchParams.get('title');
    if (paramStoryType) setStoryType(paramStoryType);
    if (paramTitle && !title) setTitle(paramTitle);
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch children on mount + pre-select from ?person= param ───────
  useEffect(() => {
    const fetchChildren = async () => {
      setLoadingChildren(true);
      try {
        const response = await axiosInstance.get('/genealogy/people/my_family_grouped/');
        const data = response.data;
        const kids = (data.children || []).map((child, idx) => ({
          ...child,
          // Assign accent colour: alternate orange/blue by index
          accentColor: idx % 2 === 0 ? 'orange' : 'blue',
        }));
        setChildren(kids);

        // Pre-select child from ?person= URL param (e.g. from Family Hub "+ Add Story")
        const personParam = searchParams.get('person') || searchParams.get('subject_member_id');
        if (personParam) {
          const matchedChild = kids.find(c => c.id === personParam);
          if (matchedChild) {
            setTier('child');
            setSelectedChildIds([personParam]);
          }
        }
      } catch (err) {
        console.error('Failed to fetch children:', err);
      } finally {
        setLoadingChildren(false);
      }
    };
    fetchChildren();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch activities for selected child ─────────────────────────────
  useEffect(() => {
    if (tier !== 'child' || selectedChildIds.length !== 1) {
      setChildActivities([]);
      setSelectedActivityId(null);
      setNewActivityOpen(false);
      return;
    }
    const childId = selectedChildIds[0];
    const child = children.find(c => c.id === childId);
    if (!child?.k_start_year) {
      setChildActivities([]);
      return;
    }
    axiosInstance.get(`/activities/${childId}/`)
      .then(res => setChildActivities(res.data?.activities || []))
      .catch(() => setChildActivities([]));
  }, [tier, selectedChildIds, children]);

  // ── Cleanup preview URLs on unmount ──────────────────────────────────
  useEffect(() => {
    return () => {
      localMediaFiles.forEach(media => {
        if (media.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(media.preview);
        }
      });
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Tier change handler ──────────────────────────────────────────────
  const handleTierChange = (newTier) => {
    setTier(newTier);
    if (newTier === 'child' && selectedChildIds.length === 0 && children.length > 0) {
      setSelectedChildIds([children[0].id]);
    }
    if (newTier === 'mine') {
      setSelectedChildIds([]);
    }
    setFieldErrors(prev => ({ ...prev, subject: undefined }));
    // Reset school year state when switching tiers
    setSchoolYearOverride(null);
    setSelectedActivityId(null);
    setNewActivityOpen(false);
    setNewActivity({ category: '', name: '', teamName: '' });
  };

  // ── Child selection handlers ─────────────────────────────────────────
  const handleToggleChild = (childId) => {
    setSelectedChildIds(prev =>
      prev.includes(childId)
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
    setFieldErrors(prev => ({ ...prev, subject: undefined }));
  };

  const handleToggleAll = () => {
    if (selectedChildIds.length === children.length) {
      setSelectedChildIds([]);
    } else {
      setSelectedChildIds(children.map(c => c.id));
    }
  };

  // ── Media handlers ───────────────────────────────────────────────────
  const handleOpenMediaCapture = () => setIsMediaCaptureOpen(true);
  const handleCloseMediaCapture = () => setIsMediaCaptureOpen(false);

  const handleMediaCaptured = (mediaData) => {
    handleFileUpload(mediaData.file, mediaData.type);
  };

  const handleFileUpload = (file, mediaType) => {
    const previewUrl = URL.createObjectURL(file);
    const localId = `local-${Date.now()}-${Math.random()}`;
    const localFile = {
      id: localId,
      file: file,
      preview: previewUrl,
      name: file.name,
      size: file.size,
      type: file.type,
      mediaType: mediaType,
      uploadStatus: 'uploading', // uploading | uploaded | failed
      uploadProgress: 0,
      uploadEta: null, // estimated seconds remaining
      mediaFileId: null, // set after upload completes
    };
    setLocalMediaFiles(prev => [...prev, localFile]);

    // Upload immediately to /api/media/files/
    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', file.name);
    formData.append('media_type', mediaType);
    formData.append('is_private', 'true');

    const uploadStartTime = Date.now();
    const recentSpeeds = []; // rolling window for smoothing

    axiosInstance.post('/media/files/', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        const pct = Math.round((e.loaded * 100) / (e.total || 1));
        const elapsed = (Date.now() - uploadStartTime) / 1000;
        let eta = null;

        if (elapsed > 1.5 && e.loaded > 0 && pct < 100) {
          const speed = e.loaded / elapsed; // bytes per second
          recentSpeeds.push(speed);
          if (recentSpeeds.length > 5) recentSpeeds.shift(); // keep last 5
          const avgSpeed = recentSpeeds.reduce((a, b) => a + b, 0) / recentSpeeds.length;
          const remaining = (e.total || file.size) - e.loaded;
          eta = Math.ceil(remaining / avgSpeed);
        }

        setLocalMediaFiles(prev => prev.map(f =>
          f.id === localId ? { ...f, uploadProgress: pct, uploadEta: pct >= 95 ? -1 : eta } : f
        ));
      },
    })
    .then(res => {
      const mediaFileId = res.data.id;
      setLocalMediaFiles(prev => prev.map(f =>
        f.id === localId ? { ...f, uploadStatus: 'uploaded', uploadProgress: 100, mediaFileId } : f
      ));
    })
    .catch(err => {
      console.error(`Upload failed for ${file.name}:`, err);
      setLocalMediaFiles(prev => prev.map(f =>
        f.id === localId ? { ...f, uploadStatus: 'failed', uploadProgress: 0 } : f
      ));
    });
  };

  const handleImageUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif';
    input.multiple = true;
    input.onchange = (e) => {
      Array.from(e.target.files).forEach(file => {
        if (!file.type.startsWith('image/')) return;
        if (file.size > 10 * 1024 * 1024) {
          alert(`"${file.name}" is too large (${(file.size / (1024*1024)).toFixed(1)} MB). Images must be under 10 MB.`);
          return;
        }
        handleFileUpload(file, 'image');
      });
    };
    input.click();
  };

  const handleVideoUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'video/mp4,video/avi,video/quicktime,video/wmv,video/webm,.mov';
    input.multiple = true;
    input.onchange = (e) => {
      Array.from(e.target.files).forEach(file => {
        if (!file.type.startsWith('video/')) return;
        if (file.size > 500 * 1024 * 1024) {
          alert(`"${file.name}" is too large (${(file.size / (1024*1024)).toFixed(0)} MB). Videos must be under 500 MB.`);
          return;
        }
        handleFileUpload(file, 'video');
      });
    };
    input.click();
  };

  const handleAudioUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'audio/mp3,audio/wav,audio/m4a,audio/aac,audio/ogg';
    input.multiple = true;
    input.onchange = (e) => {
      Array.from(e.target.files).forEach(file => {
        if (!file.type.startsWith('audio/')) return;
        if (file.size > 50 * 1024 * 1024) {
          alert(`"${file.name}" is too large (${(file.size / (1024*1024)).toFixed(1)} MB). Audio files must be under 50 MB.`);
          return;
        }
        handleFileUpload(file, 'audio');
      });
    };
    input.click();
  };

  const removeLocalMedia = (fileId) => {
    setLocalMediaFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      if (fileToRemove && fileToRemove.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return prev.filter(f => f.id !== fileId);
    });
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragOver(true); };
  const handleDragLeave = (e) => { e.preventDefault(); setIsDragOver(false); };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    Array.from(e.dataTransfer.files).forEach(file => {
      // Detect media type from MIME type, falling back to file extension
      // (.mov files may report empty MIME in some browsers)
      const ext = file.name.split('.').pop().toLowerCase();
      const videoExts = ['mp4', 'mov', 'avi', 'wmv', 'webm', 'mkv', 'm4v'];
      const audioExts = ['mp3', 'wav', 'aac', 'm4a', 'ogg', 'wma', 'flac'];
      let mt;
      if (file.type.startsWith('video/') || videoExts.includes(ext)) {
        mt = 'video';
      } else if (file.type.startsWith('audio/') || audioExts.includes(ext)) {
        mt = 'audio';
      } else {
        mt = 'image';
      }
      const maxMB = mt === 'video' ? 500 : mt === 'audio' ? 50 : 10;
      if (file.size > maxMB * 1024 * 1024) {
        alert(`"${file.name}" is too large (${(file.size/(1024*1024)).toFixed(0)} MB). ${mt === 'video' ? 'Videos' : mt === 'audio' ? 'Audio files' : 'Images'} must be under ${maxMB} MB.`);
        return;
      }
      handleFileUpload(file, mt);
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // ── Validation (on submit only — seniors find instant validation stressful) ──
  const validate = () => {
    const errors = {};

    if (tier === 'child' && selectedChildIds.length === 0) {
      errors.subject = 'Please select at least one child.';
    }
    if (!storyType) {
      errors.storyType = 'Please choose a story type.';
    }
    if (!title.trim()) {
      errors.title = 'Please give this story a title.';
    }
    if (!when.trim()) {
      errors.when = 'Please enter when this happened.';
    }
    if (!where.trim()) {
      errors.where = 'Please enter where this happened.';
    }
    if (!body.trim()) {
      errors.body = 'Please write something in the story field.';
    }

    setFieldErrors(errors);

    // Scroll to first error
    const firstKey = Object.keys(errors)[0];
    if (firstKey && sectionRefs[firstKey]?.current) {
      sectionRefs[firstKey].current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    return Object.keys(errors).length === 0;
  };

  // ── Submit ───────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;

    // Check if any files are still uploading
    const stillUploading = localMediaFiles.some(f => f.uploadStatus === 'uploading');
    if (stillUploading) {
      setError('Please wait for all files to finish uploading before saving.');
      return;
    }

    // Check for failed uploads
    const failedFiles = localMediaFiles.filter(f => f.uploadStatus === 'failed');
    if (failedFiles.length > 0) {
      setError(`${failedFiles.length} file(s) failed to upload. Remove them or try again.`);
      return;
    }

    setIsSubmitting(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Collect media_file_ids from already-uploaded files
      const mediaFileIds = localMediaFiles
        .filter(f => f.mediaFileId)
        .map(f => f.mediaFileId);

      const eventData = {
        template: STORY_TEMPLATE_ID,
        title: title.trim(),
        date: when.trim() || new Date().toISOString().split('T')[0],
        location: where.trim() || null,
        event_data: {
          story_type: storyType,
          tier,
          child_ids: tier === 'child' ? selectedChildIds : [],
        },
        story: body.trim(),
        story_responses: {
          what_happened: body.trim(),
          who_was_involved: tier === 'mine' ? 'Me' : 'My child',
          why_important: storyType,
        },
        is_private: false,
        sharing_scope: 'single_family',
        event_people: tier === 'child' ? selectedChildIds.map(id => ({ person: id })) : [],
        media_file_ids: mediaFileIds,
        // School year fields
        ...(schoolYearOverride != null ? { school_year_override: schoolYearOverride } : {}),
        ...(selectedActivityId ? { activity: selectedActivityId } : {}),
      };

      // Create new activity if needed (inline panel was used)
      if (newActivityOpen && newActivity.name.trim() && newActivity.category && activeChild) {
        try {
          const actPayload = {
            name: newActivity.name.trim(),
            category: newActivity.category,
          };
          // Include season data in the same request
          if (effectiveSY != null) {
            actPayload.school_year = effectiveSY;
            if (newActivity.teamName.trim()) {
              actPayload.team_name = newActivity.teamName.trim();
            }
          }
          const actRes = await axiosInstance.post(`/activities/${activeChild.id}/`, actPayload);
          eventData.activity = actRes.data.id;
        } catch (actErr) {
          console.error('Failed to create activity:', actErr);
          // Continue saving the story even if activity creation fails
        }
      }

      // No files in the request — just JSON with media_file_ids
      const response = await axiosInstance.post('/lifeevent/events/', eventData);
      const createdEvent = response.data;

      // Create a chapter Memory so the story shows in the chapter view.
      // Chapter assignment is handled server-side by assign_to_chapter()
      // unless explicitly passed via URL param (e.g. from a chapter's "Add Story" button).
      let chapterParam = searchParams.get('chapter') || undefined;
      const personParam = searchParams.get('person');
      const childPersonId = personParam || (tier === 'child' && selectedChildIds.length > 0 ? selectedChildIds[0] : null);
      const targetPersonId = childPersonId || null; // null = self

      {
        try {
          const storyDate = when.trim() || new Date().toISOString().split('T')[0];
          const storyYear = parseInt(storyDate.substring(0, 4), 10);
          await axiosInstance.post('/chapter-memories/', {
            chapter: chapterParam,
            title: title.trim(),
            year: storyYear,
            text: body.trim(),
            life_event_id: createdEvent?.id ? Number(createdEvent.id) : undefined,
            person_id: targetPersonId,
          });
        } catch (memErr) {
          console.error('Failed to create chapter memory:', memErr);
        }
      }

      // Clear local media files
      setLocalMediaFiles([]);

      // Redirect to Story Saved screen
      const savedChildren = tier === 'child'
        ? selectedChildIds
            .map(id => children.find(c => c.id === id))
            .filter(Boolean)
            .map(c => ({ id: c.id, first_name: c.first_name, last_name: c.last_name }))
        : [];
      navigate(`/my-life/saved/${createdEvent.id}`, { state: { tier, children: savedChildren } });
    } catch (err) {
      console.error('Failed to save story:', err);
      setError(err.response?.data?.message || err.message || 'Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Derived values ───────────────────────────────────────────────────
  // Save is only active (accent-coloured) once every required field is filled
  // and every attached file has finished uploading.
  const missingItems = [];
  if (tier === 'child' && selectedChildIds.length === 0) missingItems.push('a child');
  if (!storyType) missingItems.push('story type');
  if (!title.trim()) missingItems.push('title');
  if (!when.trim()) missingItems.push('date');
  if (!where.trim()) missingItems.push('place');
  if (!body.trim()) missingItems.push('the story');
  const uploadingCount = localMediaFiles.filter(f => f.uploadStatus === 'uploading').length;
  const failedCount = localMediaFiles.filter(f => f.uploadStatus === 'failed').length;
  const saveDisabled = missingItems.length > 0 || uploadingCount > 0 || failedCount > 0;
  const saveHint = missingItems.length > 0
    ? `Still needed: ${missingItems.join(', ')}.`
    : uploadingCount > 0
      ? `Waiting for ${uploadingCount} file${uploadingCount > 1 ? 's' : ''} to finish uploading.`
      : failedCount > 0
        ? `${failedCount} file${failedCount > 1 ? 's' : ''} failed to upload. Remove ${failedCount > 1 ? 'them' : 'it'} to continue.`
        : null;
  const saveLabel = getSaveLabel(tier, selectedChildIds, children);

  // ── School year derived values ──────────────────────────────────────
  const activeChild = tier === 'child' && selectedChildIds.length === 1
    ? children.find(c => c.id === selectedChildIds[0])
    : null;
  // Use stored k_start_year, or derive from DOB if missing (pre-existing children)
  let childKStart = activeChild?.k_start_year ?? null;
  if (childKStart == null && activeChild?.birth_date && !activeChild?.not_in_school) {
    const derived = defaultKStartYear(new Date(activeChild.birth_date));
    if (derived && isSchoolAge(derived)) childKStart = derived;
  }
  const storyDate = when || null;
  const storySY = storyDate ? schoolYearOf(new Date(storyDate)) : null;
  const computedGrade = childKStart != null && storySY != null ? gradeForYear(childKStart, storySY) : null;
  const effectiveGrade = schoolYearOverride != null
    ? gradeForYear(childKStart, schoolYearOverride)
    : computedGrade;
  const showSchoolYearBlocks = childKStart != null && storyDate && effectiveGrade != null && effectiveGrade >= -1 && effectiveGrade <= 12;
  const effectiveSY = schoolYearOverride ?? storySY;

  return (
    <HubLayout>
      <div className="bg-background-cream min-h-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-10 pb-32 sm:pb-12">

          {/* ── Header ──────────────────────────────────────────────── */}
          <div className="flex items-center gap-3 mb-6 sm:mb-8">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="p-2 rounded-full hover:bg-gray-200 transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center"
              aria-label="Go back"
            >
              <ArrowLeft size={24} className="text-text-dark" />
            </button>
            <h1 className="text-2xl sm:text-3xl font-serif font-bold text-text-dark">
              Share a Story
            </h1>
          </div>

          {/* ── Global error ────────────────────────────────────────── */}
          {error && (
            <div className="flex items-start gap-3 px-4 py-3 mb-6 bg-red-50 border border-red-200 rounded-xl">
              <AlertCircle size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-base text-red-700">{error}</p>
            </div>
          )}

          {/* ── Section 1: Whose Story ──────────────────────────────── */}
          <section ref={sectionRefs.subject} className="mb-6">
            <h2 className="font-serif text-2xl font-semibold text-text-dark mb-1">
              Whose story is this?<RequiredMark />
            </h2>
            <p className="text-lg text-text-medium mb-6">
              Choose one — this decides where the story is saved
              {tier === 'child' && ' (select at least one child)'}
            </p>
            <WhoseStorySelector
              tier={tier}
              onTierChange={handleTierChange}
              children={children}
              selectedChildIds={selectedChildIds}
              onToggleChild={handleToggleChild}
              onToggleAll={handleToggleAll}
              accentClasses={accentClasses}
            />
            {fieldErrors.subject && (
              <p className="mt-2 text-base text-red-600 flex items-center gap-2">
                <span>&#9888;&#65039;</span> {fieldErrors.subject}
              </p>
            )}
            <div className="mt-4">
              <ContextBanner
                tier={tier}
                selectedChildIds={selectedChildIds}
                children={children}
                accentClasses={accentClasses}
                bannerText={getBannerText(tier, selectedChildIds, children)}
              />
            </div>
          </section>

          {/* ── Section 2: What Happened ──────────────────────────────── */}
          <section className="bg-white border border-border-gray rounded-2xl p-5 sm:p-6 mb-4">
            <h2 className="font-serif text-2xl font-semibold text-text-dark mb-1">
              What happened?
            </h2>
            <p className="text-lg sm:text-base text-text-medium mb-5 leading-relaxed">
              {getSectionSubtitle(tier, selectedChildIds, children)}
            </p>

            {/* Title */}
            <div ref={sectionRefs.title} className="mb-4">
              <label htmlFor="story-title" className="font-serif text-xl font-semibold text-primary-green block mb-2">
                Give this story a title<RequiredMark />
              </label>
              <input
                id="story-title"
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setFieldErrors(prev => ({ ...prev, title: undefined }));
                }}
                placeholder={getTitlePlaceholder(tier, selectedChildIds, children)}
                className={`w-full border-2 rounded-xl px-4 py-4 sm:py-3 text-lg sm:text-base text-text-dark placeholder-gray-400 focus:outline-none transition-colors ${
                  fieldErrors.title
                    ? 'border-red-400 focus:border-red-400'
                    : 'border-border-gray focus:border-primary-green'
                }`}
              />
              {fieldErrors.title && (
                <p className="text-base text-red-600 mt-2 flex items-center gap-2">
                  <span>&#9888;&#65039;</span> {fieldErrors.title}
                </p>
              )}
            </div>

            {/* When / Where */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
              <div ref={sectionRefs.when}>
                <label htmlFor="story-when" className="font-serif text-xl font-semibold text-primary-green block mb-2">
                  When did this happen?<RequiredMark />
                </label>
                <input
                  id="story-when"
                  type="date"
                  value={when}
                  onChange={(e) => { setWhen(e.target.value); setFieldErrors(prev => ({ ...prev, when: undefined })); }}
                  max={new Date().toISOString().split('T')[0]}
                  className={`w-full border-2 rounded-xl px-4 py-4 sm:py-3 text-lg sm:text-base text-text-dark placeholder-gray-400 focus:border-primary-green focus:outline-none ${
                    fieldErrors.when ? 'border-red-400' : 'border-border-gray'
                  }`}
                />
                {fieldErrors.when && (
                  <p className="text-sm text-red-600 mt-1 font-medium">
                    <span>&#9888;&#65039;</span> {fieldErrors.when}
                  </p>
                )}
                <p className="text-sm text-text-medium mt-1">Pick the date this happened</p>
              </div>
              <div ref={sectionRefs.where}>
                <label htmlFor="story-where" className="font-serif text-xl font-semibold text-primary-green block mb-2">
                  Where did it happen?<RequiredMark />
                </label>
                <input
                  id="story-where"
                  type="text"
                  value={where}
                  onChange={(e) => { setWhere(e.target.value); setFieldErrors(prev => ({ ...prev, where: undefined })); }}
                  placeholder="e.g. Our backyard, school, the lake"
                  className={`w-full border-2 rounded-xl px-4 py-4 sm:py-3 text-lg sm:text-base text-text-dark placeholder-gray-400 focus:border-primary-green focus:outline-none ${
                    fieldErrors.where ? 'border-red-400' : 'border-border-gray'
                  }`}
                />
                {fieldErrors.where && (
                  <p className="text-sm text-red-600 mt-1 font-medium">
                    <span>&#9888;&#65039;</span> {fieldErrors.where}
                  </p>
                )}
              </div>
            </div>

            {/* ── Block 1: Grade Line (school-age children only) ── */}
            {showSchoolYearBlocks && (
              <div
                aria-live="polite"
                style={{
                  background: '#F1F5E8', border: '2px solid #3E4F22', borderRadius: 12,
                  padding: '12px 14px', marginTop: 12, marginBottom: 16,
                  display: 'flex', alignItems: 'center', gap: 12,
                }}
              >
                <Backpack size={22} color="#3E4F22" style={{ flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: 16.5, fontWeight: 700, color: '#2C2F26', margin: 0 }}>
                    Goes in {gradeLabel(effectiveGrade)} &middot; {schoolYearRange(effectiveSY)}
                  </p>
                  <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: 14.5, color: '#3F4436', margin: '2px 0 0' }}>
                    {schoolYearOverride != null
                      ? <>Moved by you. <button type="button" onClick={() => setSchoolYearOverride(null)} style={{ fontWeight: 700, color: '#3E4F22', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3, padding: 0 }}>Reset</button></>
                      : 'Based on the date. Summer stories go to the grade just finished.'
                    }
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setIsGradePickerOpen(true)}
                  style={{
                    fontFamily: "'Source Sans 3', sans-serif", fontSize: 15.5, fontWeight: 700,
                    color: '#3E4F22', background: 'none', border: 'none',
                    textDecoration: 'underline', textUnderlineOffset: 3,
                    cursor: 'pointer', padding: '12px 4px', minWidth: 44, minHeight: 44,
                  }}
                >
                  Change
                </button>
              </div>
            )}

            {/* Grade picker modal (story context) */}
            {activeChild && (
              <ChangeGradeModal
                isOpen={isGradePickerOpen}
                onClose={() => setIsGradePickerOpen(false)}
                titleOverride="Which grade is this story from?"
                onSave={({ kStartYear: k, notInSchool }) => {
                  if (notInSchool || k == null) {
                    setSchoolYearOverride(null);
                  } else {
                    // Convert kStartYear to a schoolYearOverride for this story
                    // The grade they picked corresponds to schoolYear = kStartYear + grade
                    // But we store the school year itself as override
                    const pickedSY = k + (effectiveGrade != null ? effectiveGrade : 0);
                    // Actually: override is a school year, and grade = sy - kStartYear
                    // The user picked a grade, which means sy = childKStart + grade
                    // kStartYearFromGrade returns currentSY - grade, so the picked grade = currentSY - k
                    // We want the school year that corresponds to this grade for this child
                    // grade = sy - childKStart, so sy = childKStart + grade
                    // The modal returns kStartYear from kStartYearFromGrade(pickedGrade)
                    // pickedGrade = currentSY - k (where k is the returned kStartYear)
                    // So the sy for this child = childKStart + pickedGrade = childKStart + currentSY - k
                    const currentSY = schoolYearOf(new Date());
                    const pickedGrade = currentSY - k;
                    const overrideSY = childKStart + pickedGrade;
                    setSchoolYearOverride(overrideSY);
                  }
                  setIsGradePickerOpen(false);
                }}
                firstName={activeChild?.first_name || 'This child'}
                gender={activeChild?.gender || 'unknown'}
                computedGrade={computedGrade}
                currentKStartYear={childKStart}
              />
            )}

            {/* Story Type */}
            <div ref={sectionRefs.storyType} className="mb-4">
              <label className="font-serif text-xl font-semibold text-primary-green block mb-2">
                What kind of story?<RequiredMark />
              </label>
              <StoryTypeGrid
                selected={storyType}
                onSelect={(v) => {
                  setStoryType(v);
                  setFieldErrors(prev => ({ ...prev, storyType: undefined }));
                }}
                accentClasses={accentClasses}
              />
              {fieldErrors.storyType && (
                <p className="text-base text-red-600 mt-2 flex items-center gap-2">
                  <span>&#9888;&#65039;</span> {fieldErrors.storyType}
                </p>
              )}
            </div>

            {/* ── Block 2: Activity Picker (school-age children only) ── */}
            {showSchoolYearBlocks && (
              <div style={{
                border: '2px solid #3E4F22', borderRadius: 14, padding: 16,
                marginTop: 22, marginBottom: 16,
              }}>
                <h3 className="font-serif font-bold text-primary-green" style={{ fontSize: 'inherit', margin: '0 0 4px' }}>
                  Was this part of an activity?{' '}
                  <span style={{ fontWeight: 400, fontSize: 16, color: '#555', fontFamily: "'Source Sans 3', sans-serif" }}>(optional)</span>
                </h3>
                <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: 15, color: '#555', margin: '0 0 12px' }}>
                  Club, scouts, a team or a sport. Leave blank if not.
                </p>

                {/* Activity chips */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }} role="radiogroup" aria-label="Select an activity">
                  {childActivities.map(act => {
                    const isSelected = selectedActivityId === act.id;
                    return (
                      <button
                        key={act.id}
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        onClick={() => {
                          setSelectedActivityId(isSelected ? null : act.id);
                          setNewActivityOpen(false);
                        }}
                        style={{
                          fontFamily: "'Source Sans 3', sans-serif", fontSize: 16, fontWeight: 700,
                          minHeight: 48, padding: '0 16px', borderRadius: 12,
                          border: isSelected ? 'none' : '1.5px solid #9A9F8A',
                          background: isSelected ? '#3E4F22' : '#fff',
                          color: isSelected ? '#fff' : '#2C2F26',
                          cursor: 'pointer',
                          display: 'inline-flex', alignItems: 'center', gap: 6,
                        }}
                      >
                        {act.name}
                      </button>
                    );
                  })}

                  {/* + New activity chip */}
                  <button
                    type="button"
                    onClick={() => {
                      setNewActivityOpen(!newActivityOpen);
                      setSelectedActivityId(null);
                      if (!newActivityOpen) setNewActivity({ category: '', name: '', teamName: '' });
                    }}
                    style={{
                      fontFamily: "'Source Sans 3', sans-serif", fontSize: 16, fontWeight: 700,
                      minHeight: 48, padding: '0 16px', borderRadius: 12,
                      border: '1.5px dashed #9A9F8A', background: '#fff',
                      color: '#3E4F22', cursor: 'pointer',
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}
                  >
                    <Plus size={16} /> New activity
                  </button>
                </div>

                {/* New activity panel */}
                {newActivityOpen && (
                  <div style={{
                    background: '#FBFAF3', border: '1.5px solid #D6D1BD', borderRadius: 12,
                    padding: 14, marginTop: 8,
                  }}>
                    <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: 15, fontWeight: 700, color: '#2C2F26', margin: '0 0 8px' }}>
                      What kind?
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                      {[
                        { key: 'club', label: 'Club', icon: Users },
                        { key: 'scouts', label: 'Scouts', icon: Tent },
                        { key: 'team_sport', label: 'Team sport', icon: Trophy },
                        { key: 'individual_sport', label: 'Individual sport', icon: Medal },
                      ].map(cat => {
                        const Icon = cat.icon;
                        const isSelected = newActivity.category === cat.key;
                        return (
                          <button
                            key={cat.key}
                            type="button"
                            onClick={() => setNewActivity(prev => ({ ...prev, category: cat.key }))}
                            style={{
                              minHeight: 48, borderRadius: 12,
                              border: 'none', cursor: 'pointer',
                              background: isSelected ? '#3E4F22' : '#fff',
                              color: isSelected ? '#fff' : '#2C2F26',
                              boxShadow: isSelected ? 'none' : 'inset 0 0 0 1.5px #D6D1BD',
                              fontFamily: "'Source Sans 3', sans-serif", fontSize: 15, fontWeight: 700,
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            }}
                          >
                            <Icon size={18} /> {cat.label}
                          </button>
                        );
                      })}
                    </div>

                    <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: 15, fontWeight: 700, color: '#2C2F26', margin: '0 0 6px' }}>
                      Name
                    </p>
                    <input
                      type="text"
                      value={newActivity.name}
                      onChange={e => setNewActivity(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g. Little League, Cub Scouts"
                      style={{
                        width: '100%', minHeight: 52, border: '2px solid #9A9F8A', borderRadius: 12,
                        padding: '0 14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: 16,
                        marginBottom: 10, boxSizing: 'border-box',
                      }}
                    />

                    {/* Team/troop field — hide for club and individual_sport */}
                    {(newActivity.category === 'team_sport' || newActivity.category === 'scouts') && (
                      <>
                        <p style={{ fontFamily: "'Source Sans 3', sans-serif", fontSize: 15, fontWeight: 700, color: '#2C2F26', margin: '0 0 6px' }}>
                          {newActivity.category === 'scouts' ? 'Troop / den (optional)' : 'Team this season (optional)'}
                        </p>
                        <input
                          type="text"
                          value={newActivity.teamName}
                          onChange={e => setNewActivity(prev => ({ ...prev, teamName: e.target.value }))}
                          placeholder={newActivity.category === 'scouts' ? 'e.g. Troop 42' : 'e.g. Tigers U10'}
                          style={{
                            width: '100%', minHeight: 52, border: '2px solid #9A9F8A', borderRadius: 12,
                            padding: '0 14px', fontFamily: "'Source Sans 3', sans-serif", fontSize: 16,
                            boxSizing: 'border-box',
                          }}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Body */}
            <div ref={sectionRefs.body}>
              <label htmlFor="story-body" className="font-serif text-xl font-semibold text-primary-green block mb-2">
                {getBodyLabel(tier, selectedChildIds, children)}<RequiredMark />
              </label>
              <textarea
                id="story-body"
                value={body}
                onChange={(e) => {
                  setBody(e.target.value);
                  setFieldErrors(prev => ({ ...prev, body: undefined }));
                }}
                placeholder={getBodyPlaceholder(tier)}
                rows={6}
                className={`w-full border-2 rounded-xl px-4 py-4 sm:py-3 text-lg sm:text-base text-text-dark placeholder-gray-400 focus:outline-none resize-y min-h-[150px] sm:min-h-[120px] leading-relaxed transition-colors ${
                  fieldErrors.body
                    ? 'border-red-400 focus:border-red-400'
                    : 'border-border-gray focus:border-primary-green'
                }`}
              />
              <p className="text-sm text-text-medium mt-2">
                &#128172; Write like you're telling this story at the dinner table
              </p>
              {fieldErrors.body && (
                <p className="text-base text-red-600 mt-2 flex items-center gap-2">
                  <span>&#9888;&#65039;</span> {fieldErrors.body}
                </p>
              )}
            </div>
          </section>

          {/* ── Section 3: Media ──────────────────────────────────────── */}
          <section className="bg-white border border-border-gray rounded-2xl p-5 sm:p-6 mb-4">
            <h2 className="font-serif text-2xl font-semibold text-text-dark mb-1">
              Add a photo or recording
            </h2>
            <p className="text-lg sm:text-base text-text-medium mb-5">
              Optional — but a photo or voice note brings the story to life
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {/* Record Media */}
              <button type="button" onClick={handleOpenMediaCapture}
                className="p-4 rounded-xl border-2 border-gray-300 hover:border-green-500 hover:shadow-lg bg-white transition-all group">
                <div className="flex flex-col items-center">
                  <div className="relative mb-2">
                    <Video className="w-8 h-8 text-green-600 group-hover:text-green-700" />
                    <Mic className="w-6 h-6 text-green-500 absolute -bottom-1 -right-1" />
                  </div>
                  <div className="font-semibold text-sm text-gray-800">Record</div>
                  <div className="text-xs text-gray-500">Audio/Video</div>
                </div>
              </button>

              {/* Add Photos */}
              <button type="button" onClick={handleImageUpload}
                className="p-4 rounded-xl border-2 border-gray-300 hover:border-blue-500 hover:shadow-lg bg-white transition-all group">
                <div className="flex flex-col items-center">
                  <Camera className="w-10 h-10 text-blue-600 group-hover:text-blue-700 mb-2" />
                  <div className="font-semibold text-sm text-gray-800">Add Photos</div>
                  <div className="text-xs text-gray-500">Upload Images</div>
                </div>
              </button>

              {/* Add Videos */}
              <button type="button" onClick={handleVideoUpload}
                className="p-4 rounded-xl border-2 border-gray-300 hover:border-purple-500 hover:shadow-lg bg-white transition-all group">
                <div className="flex flex-col items-center">
                  <Video className="w-10 h-10 text-purple-600 group-hover:text-purple-700 mb-2" />
                  <div className="font-semibold text-sm text-gray-800">Add Videos</div>
                  <div className="text-xs text-gray-500">Upload Files</div>
                </div>
              </button>

              {/* Add Audio */}
              <button type="button" onClick={handleAudioUpload}
                className="p-4 rounded-xl border-2 border-gray-300 hover:border-orange-500 hover:shadow-lg bg-white transition-all group">
                <div className="flex flex-col items-center">
                  <Mic className="w-10 h-10 text-orange-600 group-hover:text-orange-700 mb-2" />
                  <div className="font-semibold text-sm text-gray-800">Add Audio</div>
                  <div className="text-xs text-gray-500">Upload Files</div>
                </div>
              </button>
            </div>

            {/* Drag & Drop Area */}
            <div
              className={`p-6 border-2 border-dashed rounded-xl transition-all ${
                isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'
              }`}
              onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
              <div className="text-center">
                <Camera className={`w-10 h-10 mx-auto mb-2 ${isDragOver ? 'text-blue-500' : 'text-gray-400'}`} />
                <p className={`text-sm font-medium ${isDragOver ? 'text-blue-700' : 'text-gray-600'}`}>
                  {isDragOver ? 'Drop Files Here!' : 'Drag & Drop Files Here'}
                </p>
                <p className="text-xs text-gray-500 mt-1">Photos, videos, audio — or use the buttons above</p>
              </div>
            </div>

            {/* Media Files Preview */}
            {localMediaFiles.length > 0 && (
              <div className="mt-6">
                <div className="text-sm font-medium text-gray-800 mb-3">
                  Media Files ({localMediaFiles.length}):
                  {localMediaFiles.some(f => f.uploadStatus === 'uploading') && (
                    <span className="text-xs text-orange-600 ml-2">Uploading...</span>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-3">
                  {localMediaFiles.map((media) => (
                    <div key={media.id} className="relative group">
                      {media.mediaType === 'image' ? (
                        <img src={media.preview} alt={media.name}
                          className="w-full h-20 object-cover rounded-lg border border-gray-200" />
                      ) : media.mediaType === 'video' ? (
                        <div className="w-full h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center relative">
                          <video src={media.preview} className="w-full h-full object-cover rounded-lg" muted />
                          <Play className="w-6 h-6 text-white absolute drop-shadow-lg" />
                        </div>
                      ) : (
                        <div className="w-full h-20 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                          <Mic className="w-6 h-6 text-gray-400" />
                        </div>
                      )}
                      {/* Upload progress bar */}
                      {media.uploadStatus === 'uploading' && (
                        <div className="mt-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className="h-full bg-green-600 rounded-full transition-all" style={{ width: `${media.uploadProgress}%` }} />
                        </div>
                      )}
                      {/* Status indicator */}
                      <div className="flex items-center gap-1 mt-1">
                        {media.uploadStatus === 'uploaded' && (
                          <span className="text-xs text-green-600 font-medium">✓ Ready</span>
                        )}
                        {media.uploadStatus === 'uploading' && media.uploadProgress < 100 && (
                          <span className="text-xs text-orange-600">
                            {media.uploadProgress}%
                            {media.uploadEta === null && ' · Estimating...'}
                            {media.uploadEta === -1 && ' · Almost done...'}
                            {media.uploadEta > 0 && media.uploadEta < 60 && ` · ~${media.uploadEta}s left`}
                            {media.uploadEta >= 60 && ` · ~${Math.ceil(media.uploadEta / 60)} min left`}
                          </span>
                        )}
                        {media.uploadStatus === 'uploading' && media.uploadProgress >= 100 && (
                          <span className="text-xs text-orange-600">Saving to cloud...</span>
                        )}
                        {media.uploadStatus === 'failed' && (
                          <span className="text-xs text-red-600 font-medium">✕ Failed</span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 truncate">{media.name}</div>
                      <div className="text-xs text-gray-400">{formatFileSize(media.size)}</div>
                      <button type="button" onClick={() => removeLocalMedia(media.id)}
                        className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>

          {/* ── Upload status summary ──────────────────────────────── */}
          {localMediaFiles.length > 0 && localMediaFiles.some(f => f.uploadStatus === 'uploading') && (
            <p style={{ textAlign: 'center', fontSize: 14, color: '#E65100', fontWeight: 600, margin: '8px 0' }}>
              Uploading {localMediaFiles.filter(f => f.uploadStatus === 'uploading').length} file(s)... You can write your story while they upload.
            </p>
          )}

          {saveHint && (
            <p className="text-center sm:text-right text-base text-text-medium mt-2" role="status">
              <span className="text-red-600" aria-hidden="true">* </span>{saveHint}
            </p>
          )}

          {/* ── Desktop Submit Bar ─────────────────────────────────────── */}
          <div className="hidden sm:flex gap-3 justify-end mt-2">
            <button
              type="button"
              onClick={() => navigate('/stories')}
              className="px-6 py-3 rounded-xl border-2 border-border-gray bg-white text-lg sm:text-base text-text-medium font-medium hover:bg-panel-gray transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || saveDisabled}
              className={`sm:px-8 py-3 rounded-xl border-0 text-lg sm:text-base text-white font-semibold transition-all flex items-center gap-2 ${
                isSubmitting || saveDisabled
                  ? 'bg-border-gray cursor-not-allowed opacity-60'
                  : `${accentClasses.bgSolid} hover:opacity-90`
              }`}
            >
              {isSubmitting && <Loader2 size={20} className="animate-spin" />}
              {isSubmitting ? 'Saving...' : saveLabel}
            </button>
          </div>
        </div>

        {/* ── Mobile Sticky Submit Bar ──────────────────────────────────── */}
        <div className="fixed sm:hidden bottom-0 left-0 right-0 bg-white border-t border-border-gray px-4 py-4 pb-safe flex gap-3 z-50">
          <button
            type="button"
            onClick={() => navigate('/stories')}
            className="px-6 py-4 rounded-xl border-2 border-border-gray bg-white text-lg text-text-medium font-medium hover:bg-panel-gray transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting || saveDisabled}
            className={`flex-1 py-4 rounded-xl border-0 text-lg text-white font-semibold transition-all flex items-center justify-center gap-2 ${
              isSubmitting || saveDisabled
                ? 'bg-border-gray cursor-not-allowed opacity-60'
                : `${accentClasses.bgSolid} hover:opacity-90`
            }`}
          >
            {isSubmitting && <Loader2 size={20} className="animate-spin" />}
            {isSubmitting ? 'Saving...' : saveLabel}
          </button>
        </div>
      </div>

      {/* MediaCapture Modal */}
      <MediaCapture
        isOpen={isMediaCaptureOpen}
        onClose={handleCloseMediaCapture}
        onMediaCaptured={handleMediaCaptured}
      />
    </HubLayout>
  );
};

export default TellAStory;
